'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import MaskedInput from './MaskedInput';
import { validateCNPJ } from '@/lib/validators';
import { interestOptions } from '@/lib/constants';
import FieldWrapper from './FieldWrapper';
import { supabase } from '@/lib/supabase';

interface PJFormData {
  cnpj: string;
  razaoSocial: string;
  nomeContato: string;
  email: string;
  telefone: string;
  celular: string;
  interesse: string;
  informacoes_complementares: string;
}

interface Props {
  onSuccess: () => void;
}

export default function PessoaJuridicaForm({ onSuccess }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<PJFormData>({ mode: 'onBlur' });

  const cnpjValue = watch('cnpj') ?? '';
  const infoCompl = watch('informacoes_complementares') ?? '';

  // Strip formatting separators but keep letters for new CNPJ format
  const rawCNPJ = (val: string) => val.replace(/[.\-/\s]/g, '').toUpperCase();

  const handleCNPJBlur = () => {
    const raw = rawCNPJ(cnpjValue);
    if (raw.length === 14 && !validateCNPJ(raw)) {
      setError('cnpj', { message: 'CNPJ inválido — verifique os dígitos.' });
    } else if (raw.length === 14) {
      clearErrors('cnpj');
    }
  };

  const onSubmit = async (data: PJFormData) => {
    const raw = rawCNPJ(data.cnpj);
    if (!validateCNPJ(raw)) {
      setError('cnpj', { message: 'CNPJ inválido — verifique os dígitos.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const { error } = await supabase.from('adhesion_registrations').insert({
        type: 'pj',
        cnpj: raw,
        razao_social: data.razaoSocial,
        nome_contato: data.nomeContato,
        email: data.email,
        telefone: data.telefone.replace(/\D/g, ''),
        celular: data.celular.replace(/\D/g, ''),
        interesse: data.interesse,
        informacoes_complementares: data.informacoes_complementares || null,
      });

      if (error) throw error;
      onSuccess();
    } catch {
      setSubmitError(
        'Ocorreu um erro ao enviar o cadastro. Por favor, tente novamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="flex flex-col gap-5">
        {/* CNPJ — alphanumeric mask for new format */}
        <FieldWrapper
          label="CNPJ"
          required
          error={errors.cnpj?.message}
          helper="Novo formato: pode conter letras (ex.: 12.ABC.345/0001-DE). A formatação é automática."
        >
          <MaskedInput
            mask="99.999.999/9999-99"
            placeholder="00.000.000/0001-00"
            value={cnpjValue}
            onChange={(val) => {
              setValue('cnpj', val, { shouldValidate: false });
              clearErrors('cnpj');
            }}
            onBlur={handleCNPJBlur}
            hasError={!!errors.cnpj}
            aria-label="CNPJ"
            id="pj-cnpj"
            alphanumeric
          />
        </FieldWrapper>

        {/* Razão Social */}
        <FieldWrapper
          label="Razão Social"
          required
          error={errors.razaoSocial?.message}
        >
          <input
            id="pj-razao-social"
            type="text"
            placeholder="Ex.: Fazenda São João Ltda."
            className={`input-base ${errors.razaoSocial ? 'input-error' : ''}`}
            autoComplete="organization"
            {...register('razaoSocial', {
              required: 'Razão Social é obrigatória.',
              minLength: { value: 3, message: 'Razão Social deve ter ao menos 3 caracteres.' },
            })}
          />
        </FieldWrapper>

        {/* Nome do Contato Principal */}
        <FieldWrapper
          label="Nome do Contato Principal"
          required
          error={errors.nomeContato?.message}
          helper="Pessoa responsável pelo contato com nossa equipe."
        >
          <input
            id="pj-nome-contato"
            type="text"
            placeholder="Ex.: Carlos Eduardo Souza"
            className={`input-base ${errors.nomeContato ? 'input-error' : ''}`}
            autoComplete="name"
            {...register('nomeContato', {
              required: 'Nome do contato é obrigatório.',
              minLength: { value: 3, message: 'Nome deve ter ao menos 3 caracteres.' },
            })}
          />
        </FieldWrapper>

        {/* E-mail */}
        <FieldWrapper label="E-mail" required error={errors.email?.message}>
          <input
            id="pj-email"
            type="email"
            placeholder="contato@suaempresa.com.br"
            className={`input-base ${errors.email ? 'input-error' : ''}`}
            autoComplete="email"
            {...register('email', {
              required: 'E-mail é obrigatório.',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Informe um e-mail válido.',
              },
            })}
          />
        </FieldWrapper>

        {/* Telefone + Celular — two columns on sm+ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <FieldWrapper
            label="Telefone"
            required
            error={errors.telefone?.message}
          >
            <MaskedInput
              mask="(99) 9999-9999"
              placeholder="(51) 3000-0000"
              value={watch('telefone') ?? ''}
              onChange={(val) => {
                setValue('telefone', val, { shouldValidate: true });
              }}
              onBlur={() => {
                const raw = (watch('telefone') ?? '').replace(/\D/g, '');
                if (raw.length > 0 && raw.length < 10) {
                  setError('telefone', { message: 'Telefone incompleto.' });
                } else {
                  clearErrors('telefone');
                }
              }}
              hasError={!!errors.telefone}
              aria-label="Telefone"
              id="pj-telefone"
            />
            <input
              type="hidden"
              {...register('telefone', {
                required: 'Telefone é obrigatório.',
                validate: (v) => {
                  const raw = (v ?? '').replace(/\D/g, '');
                  return raw.length >= 10 || 'Telefone incompleto.';
                },
              })}
            />
          </FieldWrapper>

          <FieldWrapper
            label="Celular / WhatsApp"
            required
            error={errors.celular?.message}
          >
            <MaskedInput
              mask="(99) 99999-9999"
              placeholder="(51) 99999-9999"
              value={watch('celular') ?? ''}
              onChange={(val) => {
                setValue('celular', val, { shouldValidate: true });
              }}
              onBlur={() => {
                const raw = (watch('celular') ?? '').replace(/\D/g, '');
                if (raw.length > 0 && raw.length < 11) {
                  setError('celular', { message: 'Celular incompleto.' });
                } else {
                  clearErrors('celular');
                }
              }}
              hasError={!!errors.celular}
              aria-label="Celular / WhatsApp"
              id="pj-celular"
            />
            <input
              type="hidden"
              {...register('celular', {
                required: 'Celular / WhatsApp é obrigatório.',
                validate: (v) => {
                  const raw = (v ?? '').replace(/\D/g, '');
                  return raw.length === 11 || 'Celular incompleto — informe DDD e 9 dígitos.';
                },
              })}
            />
          </FieldWrapper>
        </div>

        {/* Interesse */}
        <FieldWrapper
          label="Interesse Principal"
          required
          error={errors.interesse?.message}
        >
          <select
            id="pj-interesse"
            className={`input-base ${errors.interesse ? 'input-error' : ''}`}
            {...register('interesse', {
              required: 'Selecione seu interesse principal.',
              validate: (v) => v !== '' || 'Selecione seu interesse principal.',
            })}
            defaultValue=""
          >
            <option value="" disabled>
              Selecione uma opção...
            </option>
            {interestOptions.map((opt) => (
              <option key={`pj-interest-${opt.value}`} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </FieldWrapper>

        {/* Informações Complementares */}
        <FieldWrapper
          label="Informações Complementares"
          error={errors.informacoes_complementares?.message}
          helper={`${infoCompl.length}/300 caracteres`}
        >
          <textarea
            id="pj-informacoes-complementares"
            placeholder="Descreva informações adicionais relevantes sobre sua empresa, rebanho ou necessidades específicas..."
            rows={4}
            className={`input-base resize-none ${errors.informacoes_complementares ? 'input-error' : ''}`}
            {...register('informacoes_complementares', {
              maxLength: { value: 300, message: 'Máximo de 300 caracteres permitidos.' },
            })}
          />
        </FieldWrapper>

        {/* Submit error */}
        {submitError && (
          <div
            className="rounded-lg px-4 py-3 text-sm flex items-start gap-2"
            style={{
              backgroundColor: 'var(--error-bg)',
              color: 'var(--error)',
              border: '1px solid rgba(192,57,43,0.2)',
            }}
            role="alert"
          >
            <span className="mt-0.5 flex-shrink-0">⚠️</span>
            <span>{submitError}</span>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full mt-2"
          style={{ height: '52px' }}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <svg
                className="spin"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <span>Enviando cadastro...</span>
            </>
          ) : (
            <>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 2L11 13" />
                <path d="M22 2L15 22 11 13 2 9l20-7z" />
              </svg>
              <span>Enviar Cadastro</span>
            </>
          )}
        </button>

        <p className="text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
          Ao enviar, você concorda com nossa{' '}
          <a
            href="https://qrcattle.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
            style={{ color: 'var(--primary)' }}
          >
            Política de Privacidade
          </a>{' '}
          (LGPD).
        </p>
      </div>
    </form>
  );
}